class VHEventSignup extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.form = this.querySelector('form');
    this.emailInput = this.form.querySelector('input[type="email"]');
    this.button = this.form.querySelector('button');
    this.buttonLabel = this.button.querySelector('[data-button-label]');
    this.buttonBusy = this.button.querySelector('[data-button-busy]');
    this.error = this.querySelector('[role="alert"]');
    this.success = this.querySelector('.vh-event-signup__success');
    this.button.disabled = false;

    if (new URLSearchParams(window.location.search).get('joined') === '1') {
      this.setAttribute('data-joined', '');
    }

    this.emailInput.addEventListener('input', () => this.clearError());
    this.form.addEventListener('submit', (event) => this.submit(event));
  }

  clearError() {
    this.error.hidden = true;
    this.error.textContent = '';
    this.emailInput.setAttribute('aria-invalid', 'false');
  }

  showError(message) {
    this.error.textContent = message;
    this.error.hidden = false;
    this.emailInput.setAttribute('aria-invalid', 'true');
    this.emailInput.focus();
  }

  setBusy(busy) {
    this.button.disabled = busy;
    this.button.setAttribute('aria-busy', String(busy));
    this.form.setAttribute('aria-busy', String(busy));
    this.emailInput.readOnly = busy;
    this.buttonLabel.hidden = busy;
    this.buttonBusy.hidden = !busy;
  }

  async submit(event) {
    event.preventDefault();
    if (this.button.disabled || this.hasAttribute('data-joined')) return;
    this.clearError();
    this.emailInput.value = this.emailInput.value.trim();
    if (!this.emailInput.checkValidity()) {
      this.showError('Please enter a valid email address.');
      return;
    }

    this.setBusy(true);
    try {
      await this.submitToKlaviyo(this.emailInput.value);
    } catch (error) {
      this.showError(error.message || 'Something went wrong. Please try again.');
      return;
    } finally {
      this.setBusy(false);
    }

    this.setAttribute('data-joined', '');
    this.form.reset();
    this.success.focus();
    // Subscription succeeded even if the browser disallows updating history.
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('joined', '1');
      window.history.replaceState(window.history.state, '', url);
    } catch {
      // Keep the confirmation visible.
    }
  }

  async submitToKlaviyo(email) {
    if (!this.dataset.publicKey.trim() || !this.dataset.listId.trim()) {
      throw new Error('Signup is temporarily unavailable. Please try again later.');
    }

    const now = new Date();
    const localDate = [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, '0'),
      String(now.getDate()).padStart(2, '0'),
    ].join('-');
    const properties = {
      signup_source: this.dataset.signupSource,
      irl_event_signup_date: localDate,
    };
    const eventName = this.dataset.eventName.trim();
    if (eventName) properties.irl_event = eventName;

    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: this.dataset.customSource,
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                properties,
              },
            },
          },
        },
        relationships: {
          list: { data: { type: 'list', id: this.dataset.listId.trim() } },
        },
      },
    };

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(this.dataset.publicKey.trim())}`,
        {
          method: 'POST',
          headers: {
            accept: 'application/vnd.api+json',
            'content-type': 'application/vnd.api+json',
            revision: '2025-10-15',
          },
          body: JSON.stringify(payload),
          signal: controller.signal,
        }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.errors?.[0]?.detail || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      if (error.name === 'AbortError' || error instanceof TypeError) {
        throw new Error('We couldn’t connect. Please check your connection and try again.');
      }
      throw error;
    } finally {
      window.clearTimeout(timeout);
    }
  }
}

if (!customElements.get('vh-event-signup')) {
  customElements.define('vh-event-signup', VHEventSignup);
}
